import { createGlobalStyle } from 'styled-components';

export const DesignSystemGlobalStyles = createGlobalStyle`
  :root {
    color-scheme: light;
  }

  [data-theme='dark'] {
    color-scheme: dark;
  }

  /*
    Scoped reset: applies only to surfaces that opt into the design system
    by adding the .ds-root class. We do NOT reset all elements globally
    because legacy screens depend on Bootstrap defaults during the migration.
  */
  .ds-root {
    font-family: var(--font-sans);
    font-size: var(--font-size-base);
    line-height: var(--line-height-normal);
    color: var(--color-text);
    background: var(--color-bg);
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
  }

  .ds-root *,
  .ds-root *::before,
  .ds-root *::after {
    box-sizing: border-box;
  }

  .ds-root h1,
  .ds-root h2,
  .ds-root h3,
  .ds-root h4 {
    font-family: var(--font-display);
    font-weight: var(--font-weight-semibold);
    line-height: var(--line-height-tight);
    letter-spacing: -0.01em;
    margin: 0;
  }

  .ds-root h5,
  .ds-root h6,
  .ds-root p,
  .ds-root ul,
  .ds-root ol {
    margin: 0;
  }

  .ds-root a {
    color: var(--color-accent-text);
    text-decoration: none;
  }

  .ds-root a:hover {
    text-decoration: underline;
  }

  .ds-root :focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
    border-radius: var(--radius-sm);
  }

  /* Native <select> option lists — token-driven for the same reason as the
     picker below: the browser paints the open popup, not us. A select styled
     with a transparent (or unset) background leaves Chromium drawing the
     option list on white while the options still inherit the theme text
     color, so dark mode renders near-white text on white at 1.1:1. Pinning
     the options to the surface tokens keeps the open list readable in both
     themes; in light mode they resolve to the white / near-black the browser
     was already using, so nothing changes there. */
  .ds-root option {
    background-color: var(--color-surface);
    color: var(--color-text);
  }

  .ds-root option:disabled {
    color: var(--color-text-tertiary);
  }

  /* react-datetime-picker / react-calendar overrides — token-driven so they
     follow the active theme instead of staying hardcoded white. Apply
     globally so the picker also looks right when rendered via portal. */
  .react-datetime-picker__wrapper {
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    padding: 6px 10px;
    color: var(--color-text);
    font-family: var(--font-sans);
    font-size: var(--font-size-base);
    transition:
      border-color var(--duration-fast) var(--ease-standard),
      box-shadow var(--duration-fast) var(--ease-standard);
  }

  .react-datetime-picker__wrapper:focus-within {
    border-color: var(--color-accent);
    box-shadow: var(--shadow-focus);
  }

  .react-datetime-picker__inputGroup__input,
  .react-datetime-picker__inputGroup__divider,
  .react-datetime-picker__inputGroup__leadingZero,
  .react-datetime-picker__inputGroup__amPm {
    color: var(--color-text) !important;
    font-family: var(--font-sans) !important;
  }

  .react-datetime-picker__inputGroup__input:focus {
    background: var(--color-accent-subtle);
    outline: none;
  }

  .react-datetime-picker__button {
    color: var(--color-text-secondary);
  }

  .react-datetime-picker__button:enabled:hover .react-datetime-picker__button__icon,
  .react-datetime-picker__button:enabled:focus .react-datetime-picker__button__icon {
    stroke: var(--color-accent);
  }

  .react-datetime-picker__calendar,
  .react-date-picker__calendar {
    z-index: 50;
  }

  .react-calendar {
    background: var(--color-surface) !important;
    color: var(--color-text) !important;
    border: 1px solid var(--color-border) !important;
    border-radius: var(--radius-lg) !important;
    box-shadow: var(--shadow-md) !important;
    font-family: var(--font-sans) !important;
    padding: var(--space-2);
  }

  .react-calendar__tile,
  .react-calendar__month-view__days__day,
  .react-calendar__month-view__weekdays__weekday,
  .react-calendar__navigation__label,
  .react-calendar__navigation__arrow {
    color: var(--color-text) !important;
    font-family: var(--font-sans) !important;
  }

  .react-calendar__month-view__weekdays__weekday abbr {
    text-decoration: none;
    font-size: var(--font-size-xs);
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-tertiary);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .react-calendar__navigation button {
    color: var(--color-text) !important;
    background: transparent !important;
    border-radius: var(--radius-sm);
    font-family: var(--font-sans) !important;
    font-weight: var(--font-weight-medium);
  }

  .react-calendar__navigation button:enabled:hover,
  .react-calendar__navigation button:enabled:focus {
    background: var(--color-surface-subtle) !important;
  }

  .react-calendar__navigation button:disabled {
    color: var(--color-text-tertiary) !important;
    background: transparent !important;
  }

  .react-calendar__tile {
    border-radius: var(--radius-sm) !important;
    transition: background var(--duration-fast) var(--ease-standard);
  }

  .react-calendar__tile:enabled:hover,
  .react-calendar__tile:enabled:focus {
    background: var(--color-surface-subtle) !important;
    color: var(--color-text) !important;
  }

  .react-calendar__tile--now {
    background: var(--color-accent-subtle) !important;
    color: var(--color-accent-text) !important;
    font-weight: var(--font-weight-semibold);
  }

  .react-calendar__tile--active,
  .react-calendar__tile--active:enabled:hover,
  .react-calendar__tile--active:enabled:focus {
    background: var(--color-accent) !important;
    color: var(--color-text-inverse) !important;
  }

  .react-calendar__tile--rangeStart,
  .react-calendar__tile--rangeEnd {
    background: var(--color-accent) !important;
    color: var(--color-text-inverse) !important;
  }

  .react-calendar__month-view__days__day--neighboringMonth {
    color: var(--color-text-tertiary) !important;
  }
`;
